import { gql } from '@apollo/client';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import { ComponentType, useEffect, useState } from 'react';
import { ScheduleTimeline_DisplayFragment } from '~components/displays/DisplayDetail/summary/__generated__/ScheduleTimeline.graphql';
import { MINUTES_IN_A_DAY } from '~components/displays/DisplayTable/constants';
import { ScheduleStandbyIcon } from '~components/schedules/icons';
import { MAX_PRIORITY_LANES } from '~components/schedules/scheduleTimeBlocks';
import {
  describeTimeBlockData,
  TIME_BLOCK_CONTENT_COLOR,
  TIME_BLOCK_META,
} from '~components/schedules/timeBlockMeta';
import { ErrorIcon, WarningIcon } from '~components/ui/icons';
import {
  ScheduleSlotWarningSeverity,
  ScheduleTimeBlock,
  TimeBlockCategory,
} from '~graphql/__generated__/types';
import { hourToDecimal, today } from '~utils/timeBlocks';

/**
 * Width of the gutter that always shows the standby icon, like the day rows of the
 * schedule detail timeline.
 */
const STANDBY_GUTTER = '32px';

const MAX_TOOLTIP_NAME_LENGTH = 30;

const WARNING_META: Record<
  ScheduleSlotWarningSeverity,
  { color: string; Icon: ComponentType<{ color: string }> }
> = {
  [ScheduleSlotWarningSeverity.Error]: { color: 'red.400', Icon: ErrorIcon },
  [ScheduleSlotWarningSeverity.Warning]: { color: 'orange.400', Icon: WarningIcon },
};

interface Props {
  display: ScheduleTimeline_DisplayFragment;
}

/**
 * Counterpart of `PowerScheduleTimeline`: shows today's `schedule` time blocks,
 * styled like the day rows of the schedule detail timeline, but non-interactive.
 */
export function ScheduleTimeline({ display }: Props) {
  const timeZone = display.timeZone?.desired ?? display.timeZone?.reported;

  // Only content plays on screen; settings such as brightness are not shown here.
  // slotIndex is 1-based into the full timeBlocks array, matching the API's slotWarnings.
  const todayBlocks =
    display.schedule?.schedule?.timeBlocks.flatMap((block, idx) =>
      block.day === today(timeZone) && block.category === TimeBlockCategory.Content
        ? [{ block, slotIndex: idx + 1 }]
        : [],
    ) ?? [];

  const warningBySlot = new Map(
    display.schedule?.slotWarnings?.map(({ slotIndex, warning }) => [slotIndex, warning]) ?? [],
  );

  const [hourPosition, setHourPosition] = useState(() => getHourPosition(timeZone));

  useEffect(() => {
    const id = setInterval(() => setHourPosition(getHourPosition(timeZone)), 60_000);
    return () => clearInterval(id);
  }, [timeZone]);

  if (!display.schedule?.schedule) return null;

  return (
    <Flex height={6} background="gray.50" borderRadius="4px">
      {/*
       * Standby keeps its own gutter so it stays visible even when a slot covers the
       * whole day. 00:00 therefore starts to its right.
       */}
      <Flex width={STANDBY_GUTTER} flexShrink={0} alignItems="center" justifyContent="center">
        <Tooltip label={t('standbyTooltip')} hasArrow>
          <ScheduleStandbyIcon width={4} height={4} color="gray.500" />
        </Tooltip>
      </Flex>
      <Box position="relative" flex={1} height="100%">
        {todayBlocks.map(({ block, slotIndex }) => {
          const warning = warningBySlot.get(slotIndex);
          const fallbackBlock =
            warning === ScheduleSlotWarningSeverity.Warning
              ? todayBlocks.find(
                  ({ block: b }) =>
                    b.priority === block.priority + 1 && b.start < block.end && b.end > block.start,
                )?.block
              : undefined;
          return (
            <ScheduleBlock
              key={blockKeyOf(block)}
              block={block}
              warning={warning}
              fallbackBlock={fallbackBlock}
            />
          );
        })}
        {/* The current hour. */}
        <Box
          position="absolute"
          top="50%"
          transform="translateY(-50%)"
          left={hourPosition}
          width="3px"
          height="35px"
          borderRadius="10px"
          backgroundColor="gray.200"
          zIndex={MAX_PRIORITY_LANES + 2}
        />
      </Box>
    </Flex>
  );
}

function useElementWidth() {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!element) return;
    setWidth(element.offsetWidth);
    const observer = new ResizeObserver(() => setWidth(element.offsetWidth));
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return { ref: setElement, width };
}

/** Minimal, non-interactive version of the schedule detail's `ScheduleTimeBlockBox`. */
function ScheduleBlock({
  block,
  warning,
  fallbackBlock,
}: {
  block: ScheduleTimeBlock;
  warning?: ScheduleSlotWarningSeverity;
  fallbackBlock?: ScheduleTimeBlock;
}) {
  const { start, end, type, priority } = block;
  const meta = TIME_BLOCK_META[type];
  const BlockIcon = meta.blockIcon;
  const { ref, width } = useElementWidth();
  const warnMeta = warning ? WARNING_META[warning] : undefined;

  const showBoth = width > 40;
  const showOne = width > 20;

  const warningLine = (() => {
    switch (warning) {
      case ScheduleSlotWarningSeverity.Error:
        return t('scheduleOutOfSyncError');
      case ScheduleSlotWarningSeverity.Warning:
        return t('scheduleOutOfSyncWarning', { blockType: t(meta.label), priority });
      default:
        return undefined;
    }
  })();

  const infoLine = (() => {
    if (warning !== ScheduleSlotWarningSeverity.Warning) return describeSlot(block);
    if (!fallbackBlock) return undefined;
    return t('schedulePlayingFromPriority', {
      blockType: t(TIME_BLOCK_META[fallbackBlock.type].label),
      name: slotName(fallbackBlock) ? ` - ${slotName(fallbackBlock)}` : '',
      priority: fallbackBlock.priority,
      timeRange: timeRangeOf(
        fallbackBlock.start > start ? fallbackBlock.start : start,
        fallbackBlock.end < end ? fallbackBlock.end : end,
      ),
    });
  })();

  const tooltipLabel = (
    <>
      {warningLine && <Text>{warningLine}</Text>}
      {infoLine && <Text>{infoLine}</Text>}
    </>
  );

  const iconContent = (() => {
    if (!showOne) return null;
    const blockIcon = <BlockIcon width={4} height={4} color={meta.blockIconColor} flexShrink={0} />;
    const warnIcon = warnMeta ? <warnMeta.Icon color={warnMeta.color} /> : null;
    return showBoth ? (
      <>
        {blockIcon}
        {warnIcon}
      </>
    ) : (
      warnIcon ?? blockIcon
    );
  })();

  const box = (
    <Box
      ref={ref}
      position="absolute"
      top={0}
      height="100%"
      left={`${hourToDecimal('start', start) * 100}%`}
      width={`${(hourToDecimal('end', end) - hourToDecimal('start', start)) * 100}%`}
      // Of the slots covering a given minute the lowest-numbered one plays, so a
      // lower priority paints over a higher one.
      zIndex={MAX_PRIORITY_LANES + 1 - priority}
      backgroundColor={meta.background}
      color={TIME_BLOCK_CONTENT_COLOR}
      display="flex"
      alignItems="center"
      justifyContent={showBoth ? 'flex-start' : 'center'}
      gap="4px"
      overflow="hidden"
      borderRadius={4}
      borderWidth={warnMeta ? 1 : 0}
      borderColor={warnMeta?.color}
      paddingLeft={showBoth ? '8px' : undefined}
    >
      {iconContent}
    </Box>
  );

  return (
    <Tooltip label={tooltipLabel} hasArrow>
      {box}
    </Tooltip>
  );
}

function truncate(text: string) {
  return text.length > MAX_TOOLTIP_NAME_LENGTH
    ? `${text.slice(0, MAX_TOOLTIP_NAME_LENGTH)}...`
    : text;
}

const slotName = (block: ScheduleTimeBlock) =>
  truncate(describeTimeBlockData(block.type, block.data, () => block.data.title ?? undefined));

const timeRangeOf = (start: string, end: string) => `${start} - ${end === '00:00' ? '24:00' : end}`;

const describeSlot = (block: ScheduleTimeBlock) =>
  [t(TIME_BLOCK_META[block.type].label), slotName(block)].filter(Boolean).join(' — ') +
  ` · ${timeRangeOf(block.start, block.end)}`;

const blockKeyOf = (block: ScheduleTimeBlock) =>
  `${block.type}|${block.priority}|${block.start}|${block.end}`;

function getHourPosition(timeZone: string | undefined) {
  const now = DateTime.now().setZone(timeZone);
  return `${((now.hour * 60 + now.minute) / MINUTES_IN_A_DAY) * 100}%`;
}

ScheduleTimeline.graphql = {
  fragments: {
    ScheduleTimeline_display: gql`
      fragment ScheduleTimeline_display on Display {
        id
        timeZone {
          reported
          desired
        }
        schedule {
          schedule {
            id
            timeBlocks {
              start
              end
              day
              type
              priority
              category
              data {
                id
                title
                mediaType
                applicationId
                source
                url
                value
              }
            }
          }
          slotWarnings {
            slotIndex
            warning
          }
        }
      }
    `,
  },
};
